import { useState } from 'react'
import { useAdminJobs, useAdminJobRuns } from '../../hooks/useAdmin'
import { DataTable } from '../../components/admin/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import type { JobRun, JobDefinition } from '../../types/admin'
import { CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function formatDuration(ms: number | null) {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function StatusBadge({ status }: { status: JobRun['status'] }) {
  if (status === 'success') return (
    <span className="flex items-center gap-1 text-[#16a34a] text-[12px] font-semibold">
      <CheckCircle2 size={13} /> success
    </span>
  )
  if (status === 'error') return (
    <span className="flex items-center gap-1 text-[#dc2626] text-[12px] font-semibold">
      <XCircle size={13} /> error
    </span>
  )
  return (
    <span className="flex items-center gap-1 text-muted text-[12px]">
      <Clock size={13} /> running
    </span>
  )
}

// ─── Job run columns ──────────────────────────────────────────────────────────

const runColumns: ColumnDef<JobRun, unknown>[] = [
  {
    accessorKey: 'jobName',
    header: 'Job',
    meta: { label: 'Job' },
    cell: ({ getValue }) => (
      <span className="font-mono text-[12px] text-ink">{String(getValue())}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    meta: { label: 'Status' },
    cell: ({ getValue }) => <StatusBadge status={getValue() as JobRun['status']} />,
  },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    meta: { label: 'Started' },
    cell: ({ getValue }) => (
      <span className="text-ink-2 text-[12px]">{formatDate(String(getValue()))}</span>
    ),
  },
  {
    accessorKey: 'durationMs',
    header: 'Duration',
    meta: { label: 'Duration (ms)' },
    cell: ({ getValue }) => (
      <span className="font-mono text-[12px] text-ink-2">{formatDuration(getValue() as number | null)}</span>
    ),
  },
  {
    accessorKey: 'result',
    header: 'Result',
    meta: { label: 'Result' },
    enableSorting: false,
    cell: ({ getValue }) => {
      const v = getValue() as Record<string, unknown> | null
      if (!v) return <span className="text-muted text-[12px]">—</span>
      return (
        <span className="font-mono text-[11px] text-ink-2 whitespace-nowrap">
          {Object.entries(v)
            .map(([k, val]) => `${k}: ${val}`)
            .join(' · ')}
        </span>
      )
    },
  },
  {
    accessorKey: 'errorMessage',
    header: 'Error',
    meta: { label: 'Error' },
    enableSorting: false,
    cell: ({ getValue }) => {
      const v = getValue() as string | null
      if (!v) return null
      return (
        <span className="text-[11px] text-[#dc2626] font-mono max-w-[300px] block truncate" title={v}>
          {v}
        </span>
      )
    },
  },
]

// ─── pg-boss control scripts ──────────────────────────────────────────────────

const PG_BOSS_SCRIPTS = `-- List all scheduled jobs and their cron expression
SELECT name, cron, timezone, created_on, updated_on
FROM pgboss.schedule;

-- View last 50 job runs across all jobs
SELECT id, name, state, created_on, started_on, completed_on,
       EXTRACT(EPOCH FROM (completed_on - started_on))::int AS duration_sec
FROM pgboss.job
ORDER BY created_on DESC
LIMIT 50;

-- Find stuck or failed jobs
SELECT id, name, state, retrylimit, retrycount, created_on, started_on
FROM pgboss.job
WHERE state IN ('failed', 'expired')
ORDER BY created_on DESC;

-- Cancel a specific stuck job by ID
DELETE FROM pgboss.job WHERE id = '<job-uuid>';

-- Manually trigger a job immediately
INSERT INTO pgboss.job (name, state, data)
VALUES ('scrape-scheduled', 'created', '{}');

-- Temporarily disable a schedule (re-enable by restarting the server)
DELETE FROM pgboss.schedule WHERE name = 'scrape-scheduled';`

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminJobsPage() {
  const { data: jobsData, isLoading: jobsLoading } = useAdminJobs()
  const { data: runsData, isLoading: runsLoading, refetch } = useAdminJobRuns()
  const [selectedJob, setSelectedJob] = useState<string | null>(null)
  const [showScripts, setShowScripts] = useState(false)

  const jobs = jobsData?.data ?? []
  const allRuns = runsData?.data ?? []
  const visibleRuns = selectedJob ? allRuns.filter((r) => r.jobName === selectedJob) : allRuns

  const lastRunByJob = Object.fromEntries(
    jobs.map((j) => {
      const last = allRuns.find((r) => r.jobName === j.name)
      return [j.name, last ?? null]
    }),
  )

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Background Jobs</h1>
          <p className="text-[13px] text-muted mt-1.5 m-0 max-w-[560px] leading-[1.5]">
            All pg-boss scheduled jobs registered at startup. Run history is logged from each job handler.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-line-soft bg-surface text-ink-2 text-[13px] cursor-pointer font-sans hover:border-line"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Job registry */}
      <div className="mb-8">
        <div className="text-[12px] font-semibold text-ink-2 uppercase tracking-wide mb-3">
          Registered jobs
        </div>
        {jobsLoading ? (
          <div className="text-muted text-[13px]">Loading…</div>
        ) : (
          <div className="grid gap-3">
            {(jobs as JobDefinition[]).map((job) => {
              const last = lastRunByJob[job.name]
              const isSelected = selectedJob === job.name
              const isDisabled = job.enabled === false
              return (
                <button
                  key={job.name}
                  type="button"
                  onClick={() => setSelectedJob(isSelected ? null : job.name)}
                  className={`text-left px-5 py-4 rounded-[14px] border transition-colors cursor-pointer font-sans w-full ${
                    isSelected
                      ? 'border-primary bg-primary-soft'
                      : isDisabled
                        ? 'border-line-soft bg-s2/50 opacity-70 hover:border-line'
                        : 'border-line-soft bg-surface hover:border-line'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[13px] font-semibold text-ink">{job.name}</span>
                        <span className="text-[11px] text-muted bg-s2 border border-line-soft px-2 py-0.5 rounded-md">
                          {job.humanSchedule}
                        </span>
                        {isDisabled && (
                          <span className="text-[11px] font-semibold text-[#dc2626] bg-[#dc2626]/10 border border-[#dc2626]/30 px-2 py-0.5 rounded-md">
                            disabled
                          </span>
                        )}
                      </div>
                      <div className="text-[12.5px] text-ink-2 leading-[1.5]">{job.description}</div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {job.touches.map((t) => (
                          <span key={t} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-s2 border border-line-soft text-muted">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      {last ? (
                        <>
                          <StatusBadge status={last.status} />
                          <div className="text-[11px] text-muted mt-1">{formatDate(last.startedAt)}</div>
                          {last.durationMs != null && (
                            <div className="font-mono text-[11px] text-muted">{formatDuration(last.durationMs)}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-[12px] text-muted">No runs yet</span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Run history */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] font-semibold text-ink-2 uppercase tracking-wide">
            Run history {selectedJob && <span className="normal-case font-normal">— {selectedJob}</span>}
          </div>
          {selectedJob && (
            <button
              type="button"
              onClick={() => setSelectedJob(null)}
              className="text-[12px] text-primary bg-transparent border-0 cursor-pointer font-sans"
            >
              Show all jobs
            </button>
          )}
        </div>
        <DataTable
          columns={runColumns}
          data={visibleRuns}
          isLoading={runsLoading}
          sortable
          filterable
          pageSize={50}
          emptyMessage="No runs recorded yet."
        />
      </div>

      {/* pg-boss control scripts */}
      <div>
        <button
          type="button"
          onClick={() => setShowScripts((v) => !v)}
          className="flex items-center gap-2 text-[13px] font-semibold text-ink-2 bg-transparent border-0 cursor-pointer font-sans mb-3 p-0 hover:text-ink"
        >
          <span className={`transition-transform ${showScripts ? 'rotate-90' : ''}`}>▶</span>
          pg-boss control scripts
        </button>
        {showScripts && (
          <div className="rounded-[14px] border border-line-soft overflow-hidden">
            <div className="bg-[#0d1117] px-5 py-4">
              <div className="text-[11px] text-[rgba(255,255,255,.4)] mb-3 font-mono uppercase tracking-wide">
                Run via psql on the server — source /app/.env first
              </div>
              <pre className="m-0 text-[12px] text-[#e6edf3] font-mono leading-[1.7] overflow-x-auto whitespace-pre">
                {PG_BOSS_SCRIPTS}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
